import { useState } from 'react';
import Layout from '../../../components/layout/Layout';

interface Regra {
  id: string;
  nome: string;
  tipo: 'produto' | 'valor' | 'quantidade' | 'periodo' | 'pdv' | 'antifraude';
  descricao: string;
  ativo: boolean;
  campanhas: string[];
  versao: number;
  ultimaAlteracao: string;
  parametros?: any;
}

interface Campanha {
  id: string;
  nome: string;
  status: 'ativa' | 'pausada' | 'concluida';
  descricao: string;
}

export default function RegrasAuditoriaPage() {
  const [campanhaSelecionada, setCampanhaSelecionada] = useState('black-friday');
  const [showNovaRegraModal, setShowNovaRegraModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [regraEditando, setRegraEditando] = useState<Regra | null>(null);
  const [tipoRegraModal, setTipoRegraModal] = useState<string>('');

  const campanhas: Campanha[] = [
    {
      id: 'black-friday',
      nome: 'Black Friday 2024',
      status: 'ativa',
      descricao: 'Campanha selecionada'
    },
    {
      id: 'campanha-verao',
      nome: 'Campanha Verão',
      status: 'ativa',
      descricao: 'Clique para selecionar'
    },
    {
      id: 'campanha-eletronicos',
      nome: 'Campanha Eletrônicos',
      status: 'pausada',
      descricao: 'Clique para selecionar'
    }
  ];

  const [regras] = useState<Regra[]>([
    {
      id: 'REG-001',
      nome: 'Smartphones',
      tipo: 'produto',
      descricao: 'Smartphones elegíveis para a campanha',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 3,
      ultimaAlteracao: '10/12/2024'
    },
    {
      id: 'REG-002',
      nome: 'Notebooks',
      tipo: 'produto',
      descricao: 'Notebooks elegíveis para a campanha',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 2,
      ultimaAlteracao: '10/12/2024'
    },
    {
      id: 'REG-003',
      nome: 'TVs',
      tipo: 'produto',
      descricao: 'TVs elegíveis para a campanha',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '10/12/2024'
    },
    {
      id: 'REG-004',
      nome: 'Tablets',
      tipo: 'produto',
      descricao: 'Tablets elegíveis para a campanha',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '10/12/2024'
    },
    {
      id: 'REG-005',
      nome: 'Smartwatches',
      tipo: 'produto',
      descricao: 'Smartwatches elegíveis para a campanha',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '10/12/2024'
    },
    {
      id: 'REG-006',
      nome: 'Valor Mínimo',
      tipo: 'valor',
      descricao: 'Valor mínimo de R$ 500',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 2,
      ultimaAlteracao: '08/12/2024',
      parametros: { minimo: 500 }
    },
    {
      id: 'REG-007',
      nome: 'Valor Máximo',
      tipo: 'valor',
      descricao: 'Valor máximo de R$ 5000',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '08/12/2024',
      parametros: { maximo: 5000 }
    },
    {
      id: 'REG-008',
      nome: 'Quantidade Máxima por Cupom',
      tipo: 'quantidade',
      descricao: 'Máximo de 3 produtos por cupom',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '05/12/2024',
      parametros: { maximo: 3 }
    },
    {
      id: 'REG-009',
      nome: 'Data de Início',
      tipo: 'periodo',
      descricao: 'Início em 01/01/2024',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '01/11/2024',
      parametros: { dataInicio: '01/01/2024' }
    },
    {
      id: 'REG-010',
      nome: 'Data de Término',
      tipo: 'periodo',
      descricao: 'Término em 01/02/2024',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 1,
      ultimaAlteracao: '01/11/2024',
      parametros: { dataFim: '01/02/2024' }
    },
    {
      id: 'REG-011',
      nome: 'Bloqueio de Duplicidade',
      tipo: 'antifraude',
      descricao: 'Impede envio da mesma nota fiscal mais vezes',
      ativo: true,
      campanhas: ['black-friday'],
      versao: 2,
      ultimaAlteracao: '01/12/2024'
    }
  ]);

  const getTipoConfig = (tipo: string) => {
    const configs = {
      produto: { 
        bg: 'bg-teal-50', 
        border: 'border-teal-200',
        icon: 'ri-shopping-bag-line', 
        iconBg: 'bg-teal-500',
        titulo: 'Produtos Elegíveis',
        descricao: 'Defina quais produtos são válidos para esta campanha'
      },
      valor: { 
        bg: 'bg-emerald-50', 
        border: 'border-emerald-200',
        icon: 'ri-money-dollar-circle-line', 
        iconBg: 'bg-emerald-500',
        titulo: 'Limites de Valor',
        descricao: 'Configure valores mínimos e máximos'
      },
      quantidade: { 
        bg: 'bg-purple-50', 
        border: 'border-purple-200',
        icon: 'ri-hashtag', 
        iconBg: 'bg-purple-500',
        titulo: 'Quantidade',
        descricao: 'Defina limites de quantidade'
      },
      periodo: { 
        bg: 'bg-blue-50', 
        border: 'border-blue-200',
        icon: 'ri-calendar-line', 
        iconBg: 'bg-blue-500',
        titulo: 'Período Válido',
        descricao: 'Configure datas de início e término'
      },
      pdv: { 
        bg: 'bg-orange-50', 
        border: 'border-orange-200',
        icon: 'ri-store-line', 
        iconBg: 'bg-orange-500',
        titulo: 'PDVs Permitidos',
        descricao: 'Selecione pontos de venda válidos'
      },
      antifraude: { 
        bg: 'bg-red-50', 
        border: 'border-red-200',
        icon: 'ri-shield-check-line', 
        iconBg: 'bg-red-500',
        titulo: 'Regras Antifraude',
        descricao: 'Configure validações de segurança'
      }
    };
    return configs[tipo as keyof typeof configs];
  };

  const regrasPorCampanha = regras.filter(r => r.campanhas.includes(campanhaSelecionada));
  
  const agrupadasPorTipo = regrasPorCampanha.reduce((acc, regra) => {
    if (!acc[regra.tipo]) {
      acc[regra.tipo] = [];
    }
    acc[regra.tipo].push(regra);
    return acc;
  }, {} as Record<string, Regra[]>);

  const tiposOrdenados = ['produto', 'valor', 'quantidade', 'periodo', 'pdv', 'antifraude'];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Regras de Auditoria</h1>
            <p className="text-sm text-gray-600 mt-1">Configure as regras de validação por campanha</p>
          </div>
          <button
            onClick={() => setShowNovaRegraModal(true)}
            className="px-5 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2 font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            <span>Nova Regra</span>
          </button>
        </div>

        {/* Seleção de Campanha */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Selecione a Campanha</h3>
          <div className="grid grid-cols-3 gap-4">
            {campanhas.map((campanha) => (
              <button
                key={campanha.id}
                onClick={() => setCampanhaSelecionada(campanha.id)}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer text-left ${
                  campanhaSelecionada === campanha.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">{campanha.nome}</h4>
                  {campanhaSelecionada === campanha.id && (
                    <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600">{campanha.descricao}</p>
                <div className="mt-3">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    campanha.status === 'ativa' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {campanha.status === 'ativa' ? 'Ativa' : 'Pausada'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Regras por Tipo */}
        <div className="grid grid-cols-1 gap-6">
          {tiposOrdenados.map((tipo) => {
            const regrasDoTipo = agrupadasPorTipo[tipo] || [];
            const config = getTipoConfig(tipo);
            
            return (
              <div key={tipo} className={`rounded-xl border-2 ${config.border} ${config.bg} overflow-hidden`}>
                {/* Header da Seção */}
                <div className="p-5 border-b-2 border-current flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 ${config.iconBg} rounded-lg flex items-center justify-center`}>
                      <i className={`${config.icon} text-white text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{config.titulo}</h3>
                      <p className="text-xs text-gray-600 mt-0.5">{config.descricao}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setTipoRegraModal(tipo);
                      setShowEditModal(true);
                      setRegraEditando(null);
                    }}
                    className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium flex items-center space-x-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line"></i>
                    </div>
                    <span>Editar</span>
                  </button>
                </div>

                {/* Lista de Regras */}
                <div className="p-5">
                  {regrasDoTipo.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                      {regrasDoTipo.map((regra) => (
                        <div
                          key={regra.id}
                          className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                            <span className="text-sm font-medium text-gray-900">{regra.nome}</span>
                          </div>
                          <button
                            onClick={() => {
                              setRegraEditando(regra);
                              setTipoRegraModal(tipo);
                              setShowEditModal(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-more-2-fill"></i>
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className={`${config.icon} text-gray-400 text-xl`}></i>
                      </div>
                      <p className="text-sm text-gray-500">Nenhuma regra configurada</p>
                      <button
                        onClick={() => {
                          setTipoRegraModal(tipo);
                          setShowEditModal(true);
                          setRegraEditando(null);
                        }}
                        className="mt-3 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                      >
                        Adicionar primeira regra
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Nova Regra - Seleção de Tipo */}
      {showNovaRegraModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Selecione o Tipo de Regra</h3>
              <button
                onClick={() => setShowNovaRegraModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-600"></i>
                </div>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {tiposOrdenados.map((tipo) => {
                const config = getTipoConfig(tipo);
                return (
                  <button
                    key={tipo}
                    onClick={() => {
                      setTipoRegraModal(tipo);
                      setShowNovaRegraModal(false);
                      setShowEditModal(true);
                      setRegraEditando(null);
                    }}
                    className={`p-5 rounded-xl border-2 ${config.border} ${config.bg} hover:shadow-md transition-all cursor-pointer text-left`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 ${config.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${config.icon} text-white text-xl`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{config.titulo}</h4>
                        <p className="text-xs text-gray-600">{config.descricao}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {tipoRegraModal && (
                  <div className={`w-10 h-10 ${getTipoConfig(tipoRegraModal).iconBg} rounded-lg flex items-center justify-center`}>
                    <i className={`${getTipoConfig(tipoRegraModal).icon} text-white text-lg`}></i>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900">
                  {regraEditando ? 'Editar Regra' : getTipoConfig(tipoRegraModal).titulo}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setRegraEditando(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-gray-600"></i>
                </div>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Nome da Regra</label>
                <input
                  type="text"
                  defaultValue={regraEditando?.nome}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Ex: Smartphones"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Descrição</label>
                <textarea
                  defaultValue={regraEditando?.descricao}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Descreva a regra..."
                />
              </div>

              {tipoRegraModal === 'valor' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Valor Mínimo</label>
                    <input
                      type="number"
                      defaultValue={regraEditando?.parametros?.minimo}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="R$ 500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Valor Máximo</label>
                    <input
                      type="number"
                      defaultValue={regraEditando?.parametros?.maximo}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="R$ 5000"
                    />
                  </div>
                </div>
              )}

              {tipoRegraModal === 'quantidade' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Quantidade Máxima por Cupom</label>
                  <input
                    type="number"
                    defaultValue={regraEditando?.parametros?.maximo || 3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="3"
                  />
                </div>
              )}

              {tipoRegraModal === 'periodo' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Data de Início</label>
                    <input
                      type="date"
                      defaultValue={regraEditando?.parametros?.dataInicio}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Data de Término</label>
                    <input
                      type="date"
                      defaultValue={regraEditando?.parametros?.dataFim}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="ativo"
                  defaultChecked={regraEditando?.ativo ?? true}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="ativo" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Regra ativa
                </label>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setRegraEditando(null);
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setRegraEditando(null);
                }}
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                {regraEditando ? 'Salvar Alterações' : 'Criar Regra'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
